import React, { useState } from "react";
import { MapPin, Zap } from "lucide-react";

// Simplified India outline path (stylised) — sufficient for schematic corridor map
const INDIA_PATH = "M304 82 L339 74 L369 82 L392 102 L410 130 L418 158 L410 178 L378 188 L346 178 L328 158 L340 138 L346 118 L336 108 L326 122 L314 130 L296 138 L280 150 L266 168 L250 176 L226 176 L206 168 L188 170 L172 186 L168 210 L178 234 L192 254 L206 272 L216 296 L226 320 L238 342 L246 366 L256 388 L272 406 L288 418 L302 432 L316 448 L332 458 L342 476 L342 496 L326 512 L306 520 L294 536 L292 556 L306 572 L332 578 L358 574 L376 562 L390 542 L400 520 L406 496 L410 470 L418 448 L432 428 L444 406 L452 380 L456 352 L450 328 L438 306 L432 280 L440 256 L458 240 L474 224 L482 202 L474 180 L452 168 L426 168 L406 158 L400 138 L388 118 L370 100 L350 88 Z";

// Cities positioned in the schematic map (x,y in 640x640 viewBox)
// Ordered by phase to drive rollout narrative Gujarat → Maharashtra → North → Punjab
const CITIES = [
    // Phase 1 — Foundation (Gujarat)
    { name: "Ahmedabad", state: "Gujarat", phase: 1, x: 230, y: 265, size: "hub" },
    { name: "Vadodara", state: "Gujarat", phase: 1, x: 245, y: 285, size: "std" },
    { name: "Surat", state: "Gujarat", phase: 1, x: 232, y: 315, size: "std" },
    { name: "Rajkot", state: "Gujarat", phase: 1, x: 200, y: 285, size: "std" },
    { name: "Dahod", state: "Gujarat", phase: 1, x: 265, y: 275, size: "std" },
    // Phase 2 — Corridor (Maharashtra + MP)
    { name: "Mumbai", state: "Maharashtra", phase: 2, x: 235, y: 355, size: "hub" },
    { name: "Pune", state: "Maharashtra", phase: 2, x: 268, y: 375, size: "std" },
    { name: "Nashik", state: "Maharashtra", phase: 2, x: 268, y: 345, size: "std" },
    { name: "Indore", state: "Madhya Pradesh", phase: 2, x: 300, y: 280, size: "std" },
    { name: "Bhopal", state: "Madhya Pradesh", phase: 2, x: 322, y: 268, size: "std" },
    // Phase 3 — North (Rajasthan · Delhi · Haryana)
    { name: "Udaipur", state: "Rajasthan", phase: 3, x: 265, y: 240, size: "std" },
    { name: "Jaipur", state: "Rajasthan", phase: 3, x: 295, y: 195, size: "hub" },
    { name: "Ajmer", state: "Rajasthan", phase: 3, x: 278, y: 215, size: "std" },
    { name: "Gurugram", state: "Haryana", phase: 3, x: 330, y: 155, size: "std" },
    { name: "Delhi", state: "NCR", phase: 3, x: 337, y: 145, size: "hub" },
    { name: "Noida", state: "UP", phase: 3, x: 345, y: 152, size: "std" },
    // Phase 4 — Densification (Punjab & above)
    { name: "Chandigarh", state: "Punjab", phase: 4, x: 325, y: 105, size: "hub" },
    { name: "Ludhiana", state: "Punjab", phase: 4, x: 310, y: 100, size: "hub" },
    { name: "Amritsar", state: "Punjab", phase: 4, x: 290, y: 88, size: "hub" },
    { name: "Jalandhar", state: "Punjab", phase: 4, x: 302, y: 95, size: "std" },
    { name: "Bathinda", state: "Punjab", phase: 4, x: 305, y: 115, size: "std" },
    { name: "Patiala", state: "Punjab", phase: 4, x: 322, y: 115, size: "std" },
    { name: "Mohali", state: "Punjab", phase: 4, x: 324, y: 110, size: "std" },
    { name: "Shimla", state: "Himachal", phase: 4, x: 345, y: 92, size: "std" },
    { name: "Dehradun", state: "Uttarakhand", phase: 4, x: 360, y: 100, size: "std" },
    { name: "Srinagar", state: "J&K", phase: 4, x: 300, y: 55, size: "std" },
];

const PHASES = [
    { id: 1, label: "Phase 1 · Foundation", color: "#6abf4b", desc: "Gujarat home network", count: 5 },
    { id: 2, label: "Phase 2 · Western Corridor", color: "#4aa8a0", desc: "Maharashtra & MP", count: 5 },
    { id: 3, label: "Phase 3 · North Expansion", color: "#2b8fb0", desc: "Rajasthan · NCR · Haryana", count: 6 },
    { id: 4, label: "Phase 4 · Punjab Densification", color: "#f59e0b", desc: "Punjab, HP, Uttarakhand, J&K", count: 9 },
];

export const NetworkMap = () => {
    const [activePhase, setActivePhase] = useState(null);

    const isActive = (p) => activePhase === null || activePhase === p;
    const cityRadius = (c) => (c.size === "hub" ? 5.5 : 3.2);

    return (
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start" data-testid="e3-network-map">
            {/* Map */}
            <div className="md:col-span-8 relative rounded-2xl bg-stone-950 p-4 md:p-8 overflow-hidden">
                <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: "radial-gradient(circle at 40% 20%, #6abf4b 0px, transparent 40%), radial-gradient(circle at 70% 80%, #2b8fb0 0px, transparent 40%)" }} />
                <svg viewBox="120 40 380 560" className="relative w-full h-auto" role="img" aria-label="E3 Charge Network India rollout map">
                    <defs>
                        <linearGradient id="mapGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#6abf4b" stopOpacity="0.15" />
                            <stop offset="100%" stopColor="#2b8fb0" stopOpacity="0.10" />
                        </linearGradient>
                        <filter id="mapGlow">
                            <feGaussianBlur stdDeviation="3" result="blur" />
                            <feMerge>
                                <feMergeNode in="blur" />
                                <feMergeNode in="SourceGraphic" />
                            </feMerge>
                        </filter>
                    </defs>
                    {/* Country outline */}
                    <path d={INDIA_PATH} fill="url(#mapGrad)" stroke="#3f3f46" strokeWidth="1.2" />

                    {/* Corridor line — Gujarat → Punjab */}
                    <path
                        d="M230 265 Q265 240 295 195 Q330 155 320 110"
                        stroke="url(#mapGrad)"
                        strokeWidth="1.5"
                        strokeDasharray="3 4"
                        fill="none"
                        opacity={activePhase === null || activePhase === 1 || activePhase === 4 ? "0.5" : "0.15"}
                    />

                    {/* Cities */}
                    {CITIES.map((c) => {
                        const phase = PHASES.find((p) => p.id === c.phase);
                        const on = isActive(c.phase);
                        return (
                            <g key={c.name} opacity={on ? 1 : 0.15} className="transition-opacity duration-500">
                                {/* Pulse (only active + hub) */}
                                {c.size === "hub" && on && (
                                    <circle cx={c.x} cy={c.y} r={cityRadius(c) + 4} fill={phase.color} opacity="0.25">
                                        <animate attributeName="r" values={`${cityRadius(c) + 4};${cityRadius(c) + 12};${cityRadius(c) + 4}`} dur="2.6s" repeatCount="indefinite" />
                                        <animate attributeName="opacity" values="0.35;0;0.35" dur="2.6s" repeatCount="indefinite" />
                                    </circle>
                                )}
                                <circle cx={c.x} cy={c.y} r={cityRadius(c)} fill={phase.color} filter={c.size === "hub" ? "url(#mapGlow)" : undefined} />
                                {c.size === "hub" && c.phase !== 4 && (
                                    <text x={c.x + 8} y={c.y + 3} fill="#e7e5e4" fontSize="7" fontFamily="Manrope, sans-serif" fontWeight="500">
                                        {c.name}
                                    </text>
                                )}
                            </g>
                        );
                    })}
                </svg>

                <div className="absolute bottom-4 left-4 right-4 md:bottom-6 md:left-6 md:right-6 flex flex-wrap items-center gap-3 text-xs text-stone-400">
                    <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-white/60" />
                        <span>Charging Hub</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-white/60" />
                        <span>Standard Station</span>
                    </div>
                    <div className="hidden md:block flex-1" />
                    <div className="text-stone-500 italic">Tentative rollout · phased 24-month plan</div>
                </div>
            </div>

            {/* Phase legend */}
            <div className="md:col-span-4 space-y-3">
                <div className="e3-overline mb-2 text-stone-500">Rollout Phases</div>
                {PHASES.map((p) => (
                    <button
                        key={p.id}
                        onMouseEnter={() => setActivePhase(p.id)}
                        onMouseLeave={() => setActivePhase(null)}
                        onFocus={() => setActivePhase(p.id)}
                        onBlur={() => setActivePhase(null)}
                        data-testid={`network-phase-${p.id}`}
                        className="w-full text-left rounded-xl border border-stone-200 bg-white p-4 hover:border-stone-400 hover:-translate-y-0.5 transition-all"
                    >
                        <div className="flex items-start gap-3">
                            <span className="mt-1.5 w-3 h-3 rounded-full shrink-0" style={{ background: p.color, boxShadow: `0 0 12px ${p.color}80` }} />
                            <div className="flex-1">
                                <div className="flex items-baseline justify-between gap-3">
                                    <div className="font-serif text-lg font-medium text-stone-900">{p.label}</div>
                                    <div className="text-xs tabular-nums text-stone-500">{p.count} cities</div>
                                </div>
                                <div className="text-xs text-stone-500 mt-1">{p.desc}</div>
                            </div>
                        </div>
                    </button>
                ))}

                <div className="rounded-xl bg-amber-50 border border-amber-200 p-4">
                    <div className="flex items-center gap-2 mb-1">
                        <Zap className="w-4 h-4 text-amber-700" />
                        <div className="text-xs font-bold uppercase tracking-widest text-amber-900">Punjab & Above</div>
                    </div>
                    <div className="text-xs text-stone-700 leading-relaxed">
                        Densest network planned in Punjab and northward — Ludhiana, Amritsar, Chandigarh, Jalandhar, Bathinda, Patiala, Mohali plus Himachal, Uttarakhand and J&K.
                    </div>
                </div>
            </div>
        </div>
    );
};
